import { FC, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded'
import { Box, Card, IconButton, TableContainer, Tooltip, Typography, useTheme } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { clickableProps } from '../../helpers/interactive'
import { barOpacityForPct, relativePct } from '../../helpers/relativeMetric'
import { formatDelta } from '../../helpers/scoreDisplay'
import { deltaColor } from '../../helpers/showcase'
import { focusRingSx } from '../../helpers/styles'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { StoryPattern, TermRef } from '../../types'
import { ShowcaseGameIcons } from '../ShowcaseGameIcons/ShowcaseGameIcons'

interface StoryPatternsTableProps {
  patterns: StoryPattern[]
  /** Navigate to a pattern/sub-pattern detail view (by pattern id). */
  onSelectPattern: (patternId: string) => void
  /** Header label for the first column (e.g. "Story Pattern" or "Sub-Pattern"). */
  firstColumnLabel: string
  /** Optional parent pattern id highlighted as the lens source. */
  highlightPatternId?: string | null
  /** Render just the table without its own outlined card (e.g. inside a `SectionCard`). */
  disableCard?: boolean
  /** Hide the "Top Games" column (e.g. the game-scoped table where it's redundant). */
  hideTopGames?: boolean
  /** Optional small icon rendered before the "Monthly Δ" column header (e.g. the source game's icon). */
  changeColumnIcon?: ReactNode
  /** Optional tooltip shown when hovering the "Monthly Δ" column header. */
  changeColumnTooltip?: string
  /**
   * Optional extra column comparing each pattern against a baseline (e.g. the
   * game's genre). When omitted the table renders its default columns only.
   */
  benchmark?: {
    /** Header label for the column. */
    label: string
    /** Optional small icon rendered before the header label (e.g. the target game/genre icon). */
    icon?: ReactNode
    /** Optional tooltip shown when hovering the header (e.g. to disambiguate whose Monthly Δ this is). */
    tooltip?: string
    /** Percentage-point delta for a pattern (positive = over-indexes); absent when there is no trend. */
    accessor: (pattern: StoryPattern) => number | undefined
  }
  /**
   * Optional trailing column showing the difference between the two delta
   * columns (e.g. the source's Monthly Δ minus the benchmark column). Rendered
   * after the `benchmark` column when supplied.
   */
  difference?: {
    /** Header label for the column. */
    label: string
    /** Percentage-point difference for a pattern; absent when either side has no trend. */
    accessor: (pattern: StoryPattern) => number | undefined
  }
}

/**
 * A flattened table row. Top-level patterns are followed by their sub-patterns
 * (when expanded). Every row carries its *group's* (parent's) sort values so the
 * stable sort keeps sub-patterns clinging directly under their parent in either
 * sort direction.
 */
interface FlatPatternRow {
  id: string
  pattern: StoryPattern
  isChild: boolean
  hasChildren: boolean
  /** `label` is the group's localised name, so the name column sorts as rendered. */
  group: { label: string; creatives: number; pct: number; delta: number; benchmark: number; difference: number }
}

type RowCustomProps = {
  maxPct: number
  expanded: ReadonlySet<string>
  toggle: (patternId: string) => void
  onSelectPattern: (patternId: string) => void
  patternLabel: (pattern: TermRef) => string
  collapseLabel: string
  expandLabel: string
}

/** Sort key for an optional delta: absent deltas sort below every real value. */
const deltaSortValue = (deltaPp: number | undefined): number => deltaPp ?? Number.NEGATIVE_INFINITY

const DeltaValue: FC<{ deltaPp: number | undefined }> = ({ deltaPp }) => (
  <Box component="span" sx={{ fontWeight: 700, color: deltaColor(deltaPp) }}>
    {formatDelta(deltaPp)}
  </Box>
)

/**
 * "Relative Impressions" cell — the pattern's share scaled against the strongest
 * pattern in the table so the leader fills the bar (100%) and the rest scale
 * proportionally, matching the Creative Ads table's Relative Impressions column.
 */
const RelativeImpressionsCell: FC<{ pct: number; maxPct: number }> = ({ pct, maxPct }) => {
  const relative = relativePct(pct, maxPct)
  const barOpacity = barOpacityForPct(relative)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 80, height: 6, bgcolor: 'action.hover', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
        <Box sx={{ height: '100%', width: `${relative}%`, bgcolor: 'primary.main', opacity: barOpacity, borderRadius: 3 }} />
      </Box>
      <Typography component="span" sx={{ fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}>
        {`${relative.toFixed(0)}%`}
      </Typography>
    </Box>
  )
}

/** Column header that optionally leads with a small icon (e.g. a game icon). */
const ColumnHeaderLabel: FC<{ icon?: ReactNode; label: ReactNode }> = ({ icon, label }) =>
  icon ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
      {icon}
      <span>{label}</span>
    </Box>
  ) : (
    <>{label}</>
  )

/** Build a column header node with an optional leading icon and hover tooltip. */
const headerLabelNode = (label: ReactNode, icon?: ReactNode, tooltip?: string): ReactElement =>
  tooltip ? (
    <Tooltip title={tooltip} arrow>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <ColumnHeaderLabel icon={icon} label={label} />
      </Box>
    </Tooltip>
  ) : (
    <ColumnHeaderLabel icon={icon} label={label} />
  )

/** Full-cell clickable wrapper so the whole row opens the pattern's detail view. */
const ClickableCell: FC<{ onOpen: () => void; align?: 'left' | 'center' | 'right'; children: ReactNode }> = ({ onOpen, align = 'left', children }) => {
  const justifyContent = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start'
  return (
    <Box onClick={onOpen} sx={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer', justifyContent }}>
      {children}
    </Box>
  )
}

/**
 * Expandable parent/child table of creative story patterns, built on `GRTable`.
 * Used on the dashboard overview (top-level patterns) and inside the detail
 * views (patterns for a motivation, sub-patterns of a pattern). Column headers
 * sort the top-level patterns; expanded sub-patterns follow their parent.
 * Clicking a row opens its detail view; the chevron toggles its sub-patterns.
 */
export const StoryPatternsTable: FC<StoryPatternsTableProps> = ({
  patterns,
  onSelectPattern,
  firstColumnLabel,
  highlightPatternId,
  disableCard = false,
  hideTopGames = false,
  changeColumnIcon,
  changeColumnTooltip,
  benchmark,
  difference,
}) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel } = useUATaxonomy()
  const theme = useTheme()
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  const toggle = useCallback((patternId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(patternId)) next.delete(patternId)
      else next.add(patternId)
      return next
    })
  }, [])

  const maxPct = useMemo(() => patterns.reduce((max, pattern) => Math.max(max, pattern.sharePct), 0), [patterns])

  // Flatten patterns into parent rows followed by their (expanded) children. The
  // input order is parent-then-children per group; GRTable's stable sort keeps
  // that order for equal group keys, so children never detach from their parent.
  const rows = useMemo<FlatPatternRow[]>(() => {
    const flat: FlatPatternRow[] = []
    patterns.forEach((pattern) => {
      const hasChildren = !!(pattern.children && pattern.children.length)
      const group = {
        label: patternLabel(pattern.pattern),
        creatives: pattern.creativesCount,
        pct: pattern.sharePct,
        delta: deltaSortValue(pattern.deltaPp),
        benchmark: benchmark ? deltaSortValue(benchmark.accessor(pattern)) : 0,
        difference: difference ? deltaSortValue(difference.accessor(pattern)) : 0,
      }
      flat.push({ id: pattern.pattern.id, pattern, isChild: false, hasChildren, group })
      if (hasChildren && expanded.has(pattern.pattern.id)) {
        pattern.children?.forEach((child) => {
          flat.push({ id: `${pattern.pattern.id}::${child.pattern.id}`, pattern: child, isChild: true, hasChildren: false, group })
        })
      }
    })
    return flat
  }, [patterns, expanded, benchmark, difference, patternLabel])

  const customProps = useMemo<RowCustomProps>(
    () => ({ maxPct, expanded, toggle, onSelectPattern, patternLabel, collapseLabel: t('collapse'), expandLabel: t('expand') }),
    [maxPct, expanded, toggle, onSelectPattern, patternLabel, t]
  )

  const tableColumns = useMemo<GRTableColumn<FlatPatternRow, RowCustomProps>[]>(
    () => [
      {
        labelAccessor: firstColumnLabel,
        sortable: true,
        sortAccessor: ({ row }) => row.group.label,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
        accessor: ({ row, customTableProps }) => {
          const isExpanded = !!customTableProps?.expanded.has(row.pattern.pattern.id)
          return (
            <Box
              onClick={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', pl: row.isChild ? 4.5 : 0, fontWeight: row.isChild ? 400 : 700 }}
            >
              {row.hasChildren ? (
                <IconButton
                  size="small"
                  aria-label={isExpanded ? customTableProps?.collapseLabel : customTableProps?.expandLabel}
                  onClick={(event) => {
                    event.stopPropagation()
                    customTableProps?.toggle(row.pattern.pattern.id)
                  }}
                  sx={{
                    p: 0.5,
                    color: 'text.secondary',
                    transition: 'transform 0.1s ease, background-color 0.1s ease',
                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <KeyboardArrowRightRounded fontSize="small" />
                </IconButton>
              ) : !row.isChild ? (
                <Box sx={{ width: 30, flexShrink: 0 }} />
              ) : null}
              {/*
                Only the name is focusable, not the whole cell: the cell also
                holds the expander button, and a button nested inside another
                button hides the inner one from screen readers. Clicking
                anywhere in the cell still works for mouse users.
              */}
              <Box
                component="span"
                {...clickableProps(() => customTableProps?.onSelectPattern(row.pattern.pattern.id), { stopPropagation: true })}
                sx={focusRingSx()}
              >
                {customTableProps?.patternLabel(row.pattern.pattern)}
              </Box>
            </Box>
          )
        },
      },
      ...(hideTopGames
        ? []
        : [
            {
              labelAccessor: t('showcase_col_top_games'),
              cellProps: { align: 'center' as const, sx: { minWidth: 140 } },
              headerCellProps: { align: 'center' as const, sx: { minWidth: 140 } },
              accessor: ({ row, customTableProps }: { row: FlatPatternRow; customTableProps?: RowCustomProps }) => (
                <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
                  <ShowcaseGameIcons games={row.pattern.games} max={row.isChild ? 2 : 3} />
                </ClickableCell>
              ),
            } as GRTableColumn<FlatPatternRow, RowCustomProps>,
          ]),
      {
        labelAccessor: t('showcase_col_num_creatives'),
        sortable: true,
        sortAccessor: ({ row }) => row.group.creatives,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center' },
        accessor: ({ row, customTableProps }) => (
          <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
            {row.pattern.creativesCount}
          </ClickableCell>
        ),
      },
      {
        labelAccessor: t('col_impressions'),
        sortable: true,
        sortAccessor: ({ row }) => row.group.pct,
        sortOrder: SortOrder.DESC,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center', sx: { minWidth: 160 } },
        accessor: ({ row, customTableProps }) => (
          <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
            <RelativeImpressionsCell pct={row.pattern.sharePct} maxPct={customTableProps?.maxPct ?? 0} />
          </ClickableCell>
        ),
      },
      {
        labelAccessor: headerLabelNode(t('showcase_col_change'), changeColumnIcon, changeColumnTooltip),
        sortable: true,
        sortAccessor: ({ row }) => row.group.delta,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'center' },
        headerCellProps: { align: 'center' },
        accessor: ({ row, customTableProps }) => (
          <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
            <DeltaValue deltaPp={row.pattern.deltaPp} />
          </ClickableCell>
        ),
      },
      ...(benchmark
        ? [
            {
              labelAccessor: headerLabelNode(benchmark.label, benchmark.icon, benchmark.tooltip),
              sortable: true,
              sortAccessor: ({ row }: { row: FlatPatternRow }) => row.group.benchmark,
              defaultSortOrder: SortOrder.DESC,
              cellProps: { align: 'center' as const },
              headerCellProps: { align: 'center' as const, sx: { minWidth: 120 } },
              accessor: ({ row, customTableProps }: { row: FlatPatternRow; customTableProps?: RowCustomProps }) => (
                <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
                  <DeltaValue deltaPp={benchmark.accessor(row.pattern)} />
                </ClickableCell>
              ),
            } as GRTableColumn<FlatPatternRow, RowCustomProps>,
          ]
        : []),
      ...(difference
        ? [
            {
              labelAccessor: difference.label,
              sortable: true,
              sortAccessor: ({ row }: { row: FlatPatternRow }) => row.group.difference,
              defaultSortOrder: SortOrder.DESC,
              cellProps: { align: 'center' as const },
              headerCellProps: { align: 'center' as const, sx: { minWidth: 120 } },
              accessor: ({ row, customTableProps }: { row: FlatPatternRow; customTableProps?: RowCustomProps }) => (
                <ClickableCell align="center" onOpen={() => customTableProps?.onSelectPattern(row.pattern.pattern.id)}>
                  <DeltaValue deltaPp={difference.accessor(row.pattern)} />
                </ClickableCell>
              ),
            } as GRTableColumn<FlatPatternRow, RowCustomProps>,
          ]
        : []),
    ],
    [t, firstColumnLabel, changeColumnIcon, changeColumnTooltip, benchmark, difference, hideTopGames]
  )

  const [columns, setColumns] = useControlledColumns(tableColumns)

  const getRowStyle = useCallback(
    (row: FlatPatternRow) =>
      !row.isChild && highlightPatternId === row.pattern.pattern.id
        ? { backgroundColor: theme.palette.action.selected, boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}` }
        : {},
    [highlightPatternId, theme]
  )

  const table = (
    <GRTable rows={rows} columns={columns} onColumnsUpdated={setColumns} customProps={customProps} getRowStyle={getRowStyle} rowIdKey="id" hoverable />
  )

  if (disableCard) {
    return <TableContainer>{table}</TableContainer>
  }

  return (
    <Card variant="outlined">
      <TableContainer>{table}</TableContainer>
    </Card>
  )
}
